import analyticsKit, { consoleProvider } from '@assetplan/analytics-kit';
import posthog from 'posthog-js';
import { createFeatureFlagService, createPosthogProvider, createConsoleProvider, FLAG } from './feature-flags';
import {
    createExperimentExposureTracker,
    shouldEmitDatePick,
    shouldEmitTimePick,
    normalizeExperimentVariant,
    resolveCalendarExperimentVariant,
    buildDatePickPayload,
    buildTimePickPayload,
} from './portal_assetplan/experiment/analytics.js';

// SENTINEL del contrato signatureHash. Cualquier propiedad o URL que llegue a
// PostHog debe pasar por este sanitizador para garantizar que el token de
// referral Aura nunca viaje a analytics. El sentinel vive aquí (al lado de
// bootstrap-analytics) para que cualquier nuevo bootstrap de provider herede
// la misma protección sin tener que duplicar la regex.
const SENTINEL_PARAM = 'signatureHash';

function stripSignatureHashFromUrl(input) {
    if (typeof input !== 'string' || input.length === 0) {
        return input;
    }

    const hashIndex = input.indexOf('#');
    const fragment = hashIndex === -1 ? '' : input.slice(hashIndex);
    const withoutFragment = hashIndex === -1 ? input : input.slice(0, hashIndex);
    const queryIndex = withoutFragment.indexOf('?');

    if (queryIndex === -1) {
        return input;
    }

    const path = withoutFragment.slice(0, queryIndex);
    const query = withoutFragment.slice(queryIndex + 1);
    const remainingParams = query.split('&').filter((parameter) => {
        const key = parameter.split('=', 1)[0].replace(/\+/g, ' ');

        try {
            return decodeURIComponent(key) !== SENTINEL_PARAM;
        } catch {
            return key !== SENTINEL_PARAM;
        }
    });

    return `${path}${remainingParams.length > 0 ? `?${remainingParams.join('&')}` : ''}${fragment}`;
}

function stripSignatureHashFromProperties(properties) {
    if (!properties || typeof properties !== 'object') {
        return properties;
    }
    // Si por error alguien envía signatureHash como propiedad custom del evento,
    // lo eliminamos. También sanitizamos $current_url y $pathname si están
    // presentes y son strings.
    const next = { ...properties };
    if (typeof next.$current_url === 'string') {
        next.$current_url = stripSignatureHashFromUrl(next.$current_url);
    }
    if (typeof next.$pathname === 'string') {
        // pathname no debería traer query, pero por si acaso.
        next.$pathname = stripSignatureHashFromUrl(next.$pathname);
    }
    if (SENTINEL_PARAM in next) {
        delete next[SENTINEL_PARAM];
    }
    // Variantes camelCase que el producto podría usar en payloads custom.
    for (const key of Object.keys(next)) {
        if (typeof key === 'string' && key.toLowerCase() === SENTINEL_PARAM.toLowerCase()) {
            delete next[key];
        }
    }
    return next;
}

const buildBeforeSend = (instanceName) => (event) => {
    if (!event || typeof event !== 'object') {
        return event;
    }
    const next = { ...event };
    if (next.properties && typeof next.properties === 'object') {
        next.properties = stripSignatureHashFromProperties(next.properties);
    }
    return next;
};

const bootstrapSitioWebAnalytics = function() {
    posthog.init(import.meta.env.VITE_POSTHOG_SITIO_WEB_KEY, {
        api_host: 'https://p.assetplan.cl', // your managed reverse proxy domain
        ui_host: 'https://us.posthog.com', // necessary because you're using a proxy, this way links will point back to PostHog properly
        defaults: '2026-01-30',
        person_profiles: 'identified_only',
        before_send: buildBeforeSend('sitio_web'),
    }, 'sitio_web');

    const posthogSitioWebProvider = {
        identified: false,
        identify: function(userId, traits) {
            if (this.identified) {
                console.log('already identified');
                return;
            }
            posthog.sitio_web.identify(userId, traits);
            this.identified = true;
        },
        track: function(eventName, eventProperties) {
            posthog.sitio_web.capture(eventName, eventProperties);
        },
    }


    return analyticsKit.registerProvider(posthogSitioWebProvider);
}

const bootstrapConsoleAnalytics = function() {
    const ourConsoleProvider = {
        identified: false,
        identify: function(userId, traits) {
            if (this.identified) {
                console.log('already identified');
                return;
            }
            consoleProvider.identify(userId, traits);
            this.identified = true;
        },
        track: function(eventName, eventProperties) {
            consoleProvider.track(eventName, eventProperties);
        },
        getFeatureFlag: function(flagName) {
            return consoleProvider.getFeatureFlag?.(flagName) ?? null;
        },
        getFeatureFlagPayload: function(flagName) {
            return consoleProvider.getFeatureFlagPayload?.(flagName) ?? null;
        },
        onFeatureFlags: function(cb) {
            if (typeof cb === 'function') {
                cb();
            }
        }
    }

    const ourConsoleRegisteredProvider = analyticsKit.registerProvider(ourConsoleProvider);

    ourConsoleRegisteredProvider.getFeatureFlag = ourConsoleProvider.getFeatureFlag;
    ourConsoleRegisteredProvider.featureFlag = ourConsoleProvider.getFeatureFlag;
    ourConsoleRegisteredProvider.getFeatureFlagPayload = ourConsoleProvider.getFeatureFlagPayload;
    ourConsoleRegisteredProvider.onFeatureFlags = ourConsoleProvider.onFeatureFlags;

    return ourConsoleRegisteredProvider;
}

const bootstrapSessionlessAnalytics = function() {
    posthog.init(import.meta.env.VITE_POSTHOG_SESSIONLESS_KEY, {
        api_host: 'https://p.assetplan.cl', // your managed reverse proxy domain
        ui_host: 'https://us.posthog.com', // necessary because you're using a proxy, this way links will point back to PostHog properly
        defaults: '2026-01-30',
        person_profiles: 'identified_only',
        persistence: 'localStorage+cookie',
        cookie_expiration: 365,
        cross_subdomain_cookie: true,
        cookie_domain: '.assetplan.cl',
        before_send: buildBeforeSend('sessionless'),
    }, 'sessionless');

    const posthogSessionlessProvider = {
        identified: false,
        identify: function(userId, traits) {
            if (this.identified) {
                console.log('already identified');
                return;
            }
            posthog.sessionless.identify(userId, traits);
            this.identified = true;
        },
        track: function(eventName, eventProperties) {
            posthog.sessionless.capture(eventName, eventProperties);
        },
        getFeatureFlag: function(flagName) {
            return posthog.sessionless.getFeatureFlag(flagName);
        },
        getFeatureFlagPayload: function(flagName) {
            return posthog.sessionless.getFeatureFlagPayload(flagName);
        },
        onFeatureFlags: function(cb) {
            posthog.sessionless.onFeatureFlags(cb);
        }
        
    }

    const posthogSessionlessRegisteredProvider = analyticsKit.registerProvider(posthogSessionlessProvider);

    posthogSessionlessRegisteredProvider.getFeatureFlag = posthogSessionlessProvider.getFeatureFlag;
    posthogSessionlessRegisteredProvider.featureFlag = posthogSessionlessProvider.getFeatureFlag;
    posthogSessionlessRegisteredProvider.getFeatureFlagPayload = posthogSessionlessProvider.getFeatureFlagPayload;
    posthogSessionlessRegisteredProvider.onFeatureFlags = posthogSessionlessProvider.onFeatureFlags;

    return posthogSessionlessRegisteredProvider;
}

/**
 * Feature flags usando la instancia posthog 'sitio_web' (producción)
 * o el console provider que loguea cada llamada sin enviar datos (desarrollo).
 *
 * Override local-only: en dev/staging, si VITE_ABC_VARIANT_OVERRIDE está
 * definida, devuelve esa variante cuando se consulte el flag del experimento
 * A/B/C de agenda. Sirve para validar visualmente cada variante sin tener
 * que tocar PostHog.
 */
const bootstrapFeatureFlags = function (isProduction = true) {
    const baseProvider = isProduction
        ? createPosthogProvider(posthog.sitio_web)
        : createConsoleProvider();

    if (isProduction) {
        return createFeatureFlagService(baseProvider);
    }

    // Override local-only del experimento A/B/C de agenda.
    // La referencia via window impide que Rollup/tree-shake elimine el bloque
    // cuando VITE_ABC_VARIANT_OVERRIDE se resuelve a undefined en build.
    const override = (typeof window !== 'undefined' && window.__ABC_VARIANT_OVERRIDE__)
        ?? import.meta.env.VITE_ABC_VARIANT_OVERRIDE;
    const experiment = FLAG.ABC_TEST_NUEVO_FLUJO_DE_AGENDA;
    const hasValidOverride = [
        experiment.CONTROL,
        experiment.HORARIO_VISIBLE,
    ].includes(override);

    const wrapped = {
        onReady: (cb) => baseProvider.onReady(cb),
        isEnabled: (flag) => flag === experiment.key && hasValidOverride
            ? true
            : baseProvider.isEnabled(flag),
        getVariant: (flag, fallback) => {
            if (flag === experiment.key && hasValidOverride) {
                console.log(`[FeatureFlags] override → "${override}" for ${experiment.key}`);
                return override;
            }
            return baseProvider.getVariant(flag, fallback);
        },
    };
    return createFeatureFlagService(wrapped);
};

export { bootstrapSitioWebAnalytics, bootstrapConsoleAnalytics, bootstrapSessionlessAnalytics, bootstrapFeatureFlags };

/**
 * Expone en window los helpers puros del experimento nuevo-flujo-de-agenda
 * para que las páginas Blade (que son script-inline y no pueden importar
 * ESM cross-file) puedan delegar la lógica al módulo testeable.
 *
 * Los partials Blade encuentran aquí `window.__experiment.exposure`,
 * `window.__experiment.selection` y `window.__experiment.normalizeVariant`.
 */
if (typeof window !== 'undefined') {
    window.__experiment = Object.freeze({
        createExposureTracker: createExperimentExposureTracker,
        shouldEmitDatePick,
        shouldEmitTimePick,
        normalizeVariant: normalizeExperimentVariant,
        resolveVariant: resolveCalendarExperimentVariant,
        buildDatePickPayload,
        buildTimePickPayload,
    });
}
